import React, { useState, useEffect } from 'react';
import { Button, Alert, Card, Badge, Spinner, Modal } from 'react-bootstrap';

/**
 * MetaboliteColumnsManager - Component for managing NH4+, Gln, Osmo columns in Google Sheets
 */
const MetaboliteColumnsManager = ({ 
  projectId, 
  googleSheetsInfo, 
  onColumnsAdded,
  className = ""
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [addingColumns, setAddingColumns] = useState(false);

  // Check metabolite columns status on component mount
  useEffect(() => {
    if (projectId && googleSheetsInfo?.spreadsheet_id) {
      checkMetaboliteStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, googleSheetsInfo]);

  // API base URL - adjust based on environment  
  const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';

  const checkMetaboliteStatus = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Checking metabolite status for project:', projectId);
      console.log('📊 Google Sheets info:', googleSheetsInfo);
      
      const apiUrl = `${API_BASE}/api/projects/${projectId}/google-sheets/metabolite-columns/status`;
      console.log('📡 API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', [...response.headers.entries()]);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Status result:', result);
      
      setStatus(result);
    } catch (err) {
      console.error('💥 Error checking metabolite status:', err);
      setError(`Failed to check metabolite columns: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddColumns = async () => {
    try {
      setAddingColumns(true);
      setError('');
      
      console.log('➕ Adding metabolite columns for project:', projectId);
      console.log('📊 Spreadsheet ID:', googleSheetsInfo?.spreadsheet_id);
      
      const apiUrl = `${API_BASE}/api/projects/${projectId}/google-sheets/add-metabolite-columns`;
      const payload = {
        spreadsheet_id: googleSheetsInfo.spreadsheet_id
      };
      
      console.log('📡 API URL:', apiUrl);
      console.log('📦 Payload:', payload);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('✅ Add columns result:', result);
      
      // Refresh status
      await checkMetaboliteStatus();
      
      // Notify parent component
      if (onColumnsAdded) {
        onColumnsAdded(result);
      }
      
      setShowModal(false);
    } catch (err) {
      console.error('💥 Error adding metabolite columns:', err);
      setError(`Failed to add metabolite columns: ${err.message}`);
    } finally {
      setAddingColumns(false);
    }
  };

  const renderMetaboliteStatus = () => {
    if (!status || !status.metabolite_columns) return null;

    const metabolites = [
      { key: 'NH4+', name: 'NH4+ (Ammonia)', unit: 'mM', description: 'Ammonium concentration' },
      { key: 'Gln', name: 'Gln (Glutamine)', unit: 'mM', description: 'Glutamine concentration' },
      { key: 'Osmo', name: 'Osmo (Osmolality)', unit: 'mOsm/kg', description: 'Osmolality measurement' }
    ];

    return (
      <div className="metabolite-status">
        {metabolites.map(metabolite => {
          const colStatus = status.metabolite_columns[metabolite.key];
          const isPresent = colStatus?.exists;
          
          return (
            <div key={metabolite.key} className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded">
              <div className="d-flex align-items-center">
                {isPresent ? (
                  <span className="text-success me-2">✓</span>
                ) : (
                  <span className="text-warning me-2">⚠</span>
                )}
                <div>
                  <strong>{metabolite.name}</strong>
                  <small className="text-muted d-block">{metabolite.description}</small>
                </div>
              </div>
              <div className="text-end">
                {isPresent ? (
                  <Badge bg="success">
                    Column {colStatus.column_letter}
                  </Badge>
                ) : (
                  <Badge bg="warning">Missing</Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderActionButton = () => {
    if (!status) return null;

    const missingCount = status.missing_columns?.length || 0;
    const existingCount = status.existing_columns?.length || 0;

    if (missingCount === 0) {
      return (
        <Alert variant="success" className="mb-0">
          <span className="me-2">✓</span>
          All metabolite columns are present in your Google Sheets!
        </Alert>
      );
    }

    return (
      <div>
        <Alert variant="info" className="mb-3">
          <span className="me-2">ℹ</span>
          {missingCount} metabolite column{missingCount > 1 ? 's' : ''} missing. 
          {existingCount > 0 && ` ${existingCount} already exist.`}
        </Alert>
        
        <Button 
          variant="primary" 
          onClick={() => setShowModal(true)}
          disabled={loading || addingColumns}
          className="w-100"
        >
          <span className="me-2">+</span>
          Add Missing Metabolite Columns ({missingCount})
        </Button>
      </div>
    );
  };

  if (!projectId || !googleSheetsInfo?.spreadsheet_id) {
    return (
      <Alert variant="info" className={className}>
        <span className="me-2">ℹ</span>
        Create or link a Google Sheet first to manage metabolite columns.
      </Alert>
    );
  }

  return (
    <div className={className}>
      <Card>
        <Card.Header className="d-flex align-items-center justify-content-between">
          <h6 className="mb-0">📊 Metabolite Columns Manager</h6>
          {loading && <Spinner animation="border" size="sm" />}
        </Card.Header>
        
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          
          {status && (
            <>
              <div className="mb-3">
                <small className="text-muted">
                  Status for Google Sheet: {googleSheetsInfo.title}
                </small>
              </div>
              
              {renderMetaboliteStatus()}
              
              <div className="mt-3">
                {renderActionButton()}
              </div>
            </>
          )}
          
          {!status && !loading && (
            <Button variant="outline-primary" onClick={checkMetaboliteStatus}>
              Check Metabolite Columns Status
            </Button>
          )}
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Metabolite Columns</Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <div className="mb-3">
            <p>This will add the following columns to your Google Sheets:</p>
            
            <ul className="list-unstyled">
              {status?.missing_columns?.map(col => (
                <li key={col} className="mb-2">
                  <Badge bg="primary" className="me-2">{col}</Badge>
                  {col === 'NH4+' && 'Ammonia concentration (mM)'}
                  {col === 'Gln' && 'Glutamine concentration (mM)'}
                  {col === 'Osmo' && 'Osmolality (mOsm/kg)'}
                </li>
              ))}
            </ul>
          </div>
          
          <Alert variant="info">
            <span className="me-2">ℹ</span>
            <strong>Default values will be added:</strong>
            <ul className="mb-0 mt-2">
              <li>NH4+: 1.5 mM</li>
              <li>Gln: 3.5 mM</li>
              <li>Osmo: 300 mOsm/kg</li>
            </ul>
          </Alert>
          
          <Alert variant="warning">
            <span className="me-2">⚠</span>
            Replace these defaults with actual laboratory measurements for accurate calculations.
          </Alert>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            disabled={addingColumns}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAddColumns}
            disabled={addingColumns}
          >
            {addingColumns ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding Columns...
              </>
            ) : (
              <>
                <span className="me-2">+</span>
                Add Columns
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MetaboliteColumnsManager;
